import type { RowData, Table } from "@tanstack/react-table";
import type { DataGridRow } from "../../columns/types";
import type { DataGridFeatures } from "../../features";

/** What a row's checkbox shows: checked, mixed, or empty. */
export type RowSelectionDisplay = "all" | "some" | "none";

const isCascading = <TData extends RowData>(row: DataGridRow<TData>) =>
    row.table.options.enableSubRowSelection !== false;

/**
 * A row's checkbox state. With cascading selection a parent reads from its
 * descendants: all selected is checked, some is mixed, so selecting every
 * child one by one checks the parent too.
 */
export const getRowSelectionDisplay = <TData extends RowData>(
    row: DataGridRow<TData>,
): RowSelectionDisplay => {
    const isOwnSelected = row.getIsSelected();
    if (row.subRows.length > 0 && isCascading(row)) {
        if (row.getIsAllSubRowsSelected()) {
            return "all";
        }
        if (row.getIsSomeSelected()) {
            return "some";
        }
    }
    return isOwnSelected ? "all" : "none";
};

/**
 * Toggles a row the way its checkbox does. A mixed parent becomes fully
 * selected; `isRangeEvent` (Shift) applies the same value to every row
 * between the last toggled row and this one.
 */
export const toggleRowSelection = <TData extends RowData>({
    row,
    isRangeEvent,
}: {
    row: DataGridRow<TData>;
    isRangeEvent: boolean;
}) => {
    if (!row.getCanSelect()) {
        return;
    }
    const shouldSelect = getRowSelectionDisplay(row) !== "all";
    const handleToggle = row.getToggleSelectedHandler({
        deselectParents: isCascading(row),
    });
    handleToggle({
        target: { checked: shouldSelect },
        shiftKey: isRangeEvent,
    });
};

/** Which rows the header checkbox and Cmd+A select. */
export type DataGridSelectAllScope = "page" | "all";

/** Whether all, or only some, of the rows in `scope` are selected. */
export const getSelectAllState = <TData extends RowData>(
    table: Table<DataGridFeatures, TData>,
    scope: DataGridSelectAllScope,
) =>
    scope === "page"
        ? {
              isAll: table.getIsAllPageRowsSelected(),
              isSome: table.getIsSomePageRowsSelected(),
          }
        : {
              isAll: table.getIsAllRowsSelected(),
              isSome: table.getIsSomeRowsSelected(),
          };

/** Selects or clears every row in `scope`. */
export const toggleAllInScope = <TData extends RowData>({
    table,
    scope = "all",
    isSelected,
}: {
    table: Table<DataGridFeatures, TData>;
    scope: DataGridSelectAllScope | undefined;
    isSelected: boolean;
}) => {
    if (scope === "page") {
        table.toggleAllPageRowsSelected(isSelected);
    } else {
        table.toggleAllRowsSelected(isSelected);
    }
};
